import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { catchError, map, of } from "rxjs";
import { AuthenticationApiService } from "../../core/authentication/authentication-api.service";
import {
  AuthenticationErrors,
  defaultTokenAquisitionErrorCode,
  extraValidationRequiredErrorCode
} from "../../core/authentication/authentication-errors";
import { AuthenticationServerService } from "../../core/authentication/authentication-server.service";
import { IHttpErrorResponseBody } from "../../core/http-error-response-body.model";
import { LoadingService } from "../../core/loading-spinner/loading.service";
import { WINDOW } from "../../shared/app.window";
import { BaseComponent } from "../../shared/base.component";
import { PasswordInputComponent } from "../../shared/FormControls/password-input/password-input.component";
import { ControlsOf, removeError, requiredWithCustomMessage, setError } from "../../shared/FormControls/validators";
import { NotificationBarComponent } from "../../shared/notification-bar";
import { LoginService } from "../login/login.service";
import { PasswordPolicy, PasswordUpdateService } from "./password-update.service";
import {ExtraValidationComponent} from "./extra-validation/extra-validation.component";
import {ModalClosedEvent, ModalService} from "cool-shared/overlay";
import {GoogleOAuthError, GoogleOAuthService} from "../../core/oauth/google-oauth.service";

interface FormModel {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PASSWORD_CHANGE_ERRORS = new Map([
  ["InsufficientPasswordComplexity", "complexity-validation-failed"],
  ["CurrentPasswordIncorrect", "old-password-invalid"],
  ["UnexpectedError", "unexpected-error"]
]);

@Component({
  selector: "app-password-change",
  templateUrl: "./password-update.component.html",
  styleUrls: ["./password-update.component.scss"]
})
export class PasswordUpdateComponent extends BaseComponent implements OnInit, AfterViewInit {
  override componentTranslationRoot = "password-change";
  changeInProgress = false;
  loginInProgress = false;
  isUpdatePasswordValidationTriggered = false;
  oldPasswordTranslationRoot = this.componentTranslationRoot + ".old-password";
  newPasswordTranslationRoot = this.componentTranslationRoot + ".new-password";
  confirmPasswordTranslationRoot = this.componentTranslationRoot + ".confirm-password";

  googleAccessToken: string | null = null;

  @ViewChild("notificationBar", { static: false }) notificationBar!: NotificationBarComponent;
  @ViewChild("oldPasswordInput", { static: true }) oldPasswordInput!: PasswordInputComponent;
  @ViewChild("newPasswordInput", { static: true }) newPasswordInput!: PasswordInputComponent;
  @ViewChild("newPasswordConfirmationInput", { static: true }) newPasswordConfirmationInput!: PasswordInputComponent;

  passwordUpdateForm = this.formBuilder.group<ControlsOf<FormModel>>({
    oldPassword: this.formBuilder.control("", { validators: [requiredWithCustomMessage("required", "1")], nonNullable: true }),
    newPassword: this.formBuilder.control("", { validators: [requiredWithCustomMessage("required", "1")], nonNullable: true }),
    confirmPassword: this.formBuilder.control("", { nonNullable: true }),
  },
  {
    updateOn: "blur",
    validators: [
      passwordConfirmationMatchValidator()
    ]
  }
  );


  constructor(
    translateService: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly authenticationApiService: AuthenticationApiService,
    private readonly authenticationServerService: AuthenticationServerService,
    private readonly passwordUpdateService: PasswordUpdateService,
    private readonly loginService: LoginService,
    private readonly loadingService: LoadingService,
    private readonly modalService: ModalService,
    private readonly googleOAuthService: GoogleOAuthService,
    @Inject(WINDOW) private window: Window,

  ) {
    super(translateService);
  }

  ngAfterViewInit(): void {
    this.oldPasswordInput.focus();
  }

  ngOnInit(): void {
    this.handleGoogleCallback();

    this.passwordUpdateForm.controls.newPassword
      .addValidators([
        passwordComplexityValidator(this.passwordUpdateService.passwordPolicy),
        forbiddenWordsValidator(this.passwordUpdateService.passwordPolicy)
      ]);

    this.subscriptionsToRelease.push(
      this.passwordUpdateForm.valueChanges.subscribe(() => {
        this.notificationBar.hide();
      })
    );
  }

  get isFormSubmitDisabled(): boolean {
    return this.isUpdatePasswordValidationTriggered && (this.passwordUpdateForm.invalid || this.changeInProgress);
  }

  goBack() {
    this.window.history.back();
  }

  changePassword(): void {
    this.isUpdatePasswordValidationTriggered = true;
    if (this.passwordUpdateForm.valid) {
      this.changeInProgress = true;//this still has be here as loading service  works with programmed delays and we need to ensure that form will net be submited 2 times
      this.loadingService.show();
      this.notificationBar.hide();

      this.authenticationApiService.updatePassword(
        this.passwordUpdateService.selectedUserName,
        this.passwordUpdateForm.controls.oldPassword.value,
        this.passwordUpdateForm.controls.newPassword.value,
        this.googleAccessToken ?? undefined
      )
        .subscribe({
          next: () => {
            this.loginInProgress = true;
            this.authenticationServerService.authenticateUsingUsernameAndPassword(
              this.passwordUpdateService.selectedUserName,
              this.passwordUpdateForm.controls.newPassword.value
            )
              .pipe(
                map(token => {
                  this.loginService.login(token);
                }),
                catchError(response => of(this.handleTokenAcquisitionError(response)))
              ).subscribe();
          },
          error: (err) => this.handlePasswordUpdateError(err)
        });
    }

  }

  tryTriggerSubmit(event: Event) {
    this.oldPasswordInput.blur();
    this.newPasswordInput.blur();
    this.newPasswordConfirmationInput.blur();
    this.passwordUpdateForm.markAllAsTouched();
    (event.target as HTMLInputElement).focus();
    this.changePassword();
  }

  handleGoogleCallback(): void{
    const callbackData = this.googleOAuthService.readCallbackData();

    if (!callbackData) {
      return;
    }

    this.restorePasswords(callbackData.oldPassword, callbackData.newPassword);

    const googleOAuthResult = this.googleOAuthService.handleCallback();

    if (googleOAuthResult) {
      this.loadingService.show();

      googleOAuthResult.subscribe({
        next: (result) => {
          this.loadingService.hide();
          this.googleAccessToken = result.accessToken;

          this.changePassword();
        },
        error: (error: GoogleOAuthError) => {
          this.loadingService.hide();
          this.googleAccessToken = null;

          this.notificationBar.showErrorMessage({
            icon: "icon-meh",
            text: error.details || error.message
          });
        }
      });
    }
  }

  restorePasswords(oldPassword: string, newPassword: string): void {
    if (oldPassword) {
      this.passwordUpdateForm.controls.oldPassword.setValue(oldPassword);
    }

    if (newPassword) {
      this.passwordUpdateForm.controls.newPassword.setValue(newPassword);
      this.passwordUpdateForm.controls.confirmPassword.setValue(newPassword);
    }
  }

  redirectToGoogleLogin(): void{
    this.googleOAuthService.login({
      loginName: this.passwordUpdateService.selectedUserName,
      oldPassword: this.passwordUpdateForm.controls.oldPassword.value,
      newPassword: this.passwordUpdateForm.controls.newPassword.value
    });
  }

  showExtraValidationModal(): void {
    const reference = this.modalService.open(ExtraValidationComponent);

    reference.onClose.subscribe((result: ModalClosedEvent) => {
      if (!result.confirmed) {
        return;
      }

      this.redirectToGoogleLogin();
    });
  }

  private onRequestFinished() {
    this.changeInProgress = false;
    this.loadingService.hide();
  }

  private handleTokenAcquisitionError(err: HttpErrorResponse) {
    const responseBody = err.error as IHttpErrorResponseBody;
    const errorKey = AuthenticationErrors.getErrorTranslationKey(responseBody.Code);
    this.notificationBar.showErrorMessage({
      icon: "icon-meh",
      text: this.translate(errorKey, {}, "authentication-api")
    });
    this.onRequestFinished();
  }

  private handlePasswordUpdateError(response: HttpErrorResponse) {
    const responseBody = response.error as IHttpErrorResponseBody;

    if(responseBody.Code == extraValidationRequiredErrorCode) {
      this.showExtraValidationModal();
    }
    else{
      let textToShow = PASSWORD_CHANGE_ERRORS.get(defaultTokenAquisitionErrorCode)!;
      if (PASSWORD_CHANGE_ERRORS.has(responseBody.Code)) {
        textToShow = PASSWORD_CHANGE_ERRORS.get(responseBody.Code)!;
      }
      this.notificationBar.showErrorMessage({
        icon: "icon-meh",
        text: this.translate("update-response." + textToShow)
      });
    }

    this.onRequestFinished();
  }
}

function forbiddenWordsValidator(policy: PasswordPolicy): ValidatorFn {
  return (control: AbstractControl) => {
    const value = (control.value as string).toLowerCase();
    const matches = policy.forbiddenWords.filter(word => value.indexOf(word.toLowerCase()) > -1);

    if (matches.length > 0) {
      return { "4": "password-contains-forbidden-words" };
    }
    return null;
  };
}

function passwordComplexityValidator(policy: PasswordPolicy): ValidatorFn {
  return (control: AbstractControl) => {
    let valid = true;
    policy.testExpressions.forEach(expression => {
      const testResult = RegExp(expression).test(control.value);
      valid = valid && testResult;
    });
    if (!valid) {
      return { "2": `password-failed-validation-expressions-${policy.level.toLowerCase()}` };
    }
    return null;
  };
}

function passwordConfirmationMatchValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    const group = control as FormGroup<ControlsOf<FormModel>>;
    const password = group.controls.newPassword.value;
    const confirmPassword = group.controls.confirmPassword.value;
    const oldPassword = group.controls.oldPassword.value;
    let hasErrors = false;

    if (password == oldPassword) {
      setError(group.controls.newPassword, "3", "same-password");
      hasErrors = true;
    } else {
      removeError(group.controls.newPassword, "3");
    }

    if (password !== confirmPassword) {
      setError(group.controls.confirmPassword, "3", "password-mismatch");
      hasErrors = true;
    } else {
      removeError(group.controls.confirmPassword, "3");
    }

    if (hasErrors) {
      return {
        isValid: false
      };
    } else {
      return null;
    }

  };
}
